import { useQueries } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Task } from "@/types/task";
import EditTaskDialog from "./EditTaskDialog";
import DeleteTaskDialog from "./DeleteTaskDialog";
import TaskItem from "./TaskItem";
import { useTaskMutations } from "@/hooks/useTaskMutations";
import QuickAddTask from "./QuickAddTask";
import { logger } from "@/utils/logger";
import { Skeleton } from "./ui/skeleton";
import { EmptyState } from "./EmptyState";
import { ListTodo } from "lucide-react";

interface TaskListProps {
  tasks: Task[];
  isLoading?: boolean;
  draggable?: boolean;
  onEditTask?: (task: Task) => void;
  onDeleteTask?: (task: Task) => void;
}

const TaskList = ({
  tasks,
  isLoading = false,
  draggable = false,
  onEditTask,
  onDeleteTask
}: TaskListProps) => {
  const { completeTask, deleteTask: _deleteTaskMutation, skipOccurrence, updateTaskPriority } = useTaskMutations();

  const commentQueries = useQueries({
    queries: tasks.map((task) => ({
      queryKey: ["commentCount", task.id],
      queryFn: async () => {
        const { count, error } = await supabase
          .from("comments")
          .select("*", { count: "exact", head: true })
          .eq("task_id", task.id);
        if (error) throw error;
        return count || 0;
      },
    })),
  });

  const handleDeleteClick = (task: Task) => {
    if (onDeleteTask) {
      onDeleteTask(task);
    } else {
      logger.warn("onDeleteTask prop not provided to TaskList");
    }
  };

  const handleEditClick = (task: Task) => {
    if (onEditTask) {
      onEditTask(task);
    } else {
      logger.warn("onEditTask prop not provided to TaskList");
    }
  };

  const addToGoogleCalendar = (task: Task) => {
    const startTime = task.deadline ? new Date(task.deadline) : new Date();
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);

    const event = {
      text: task.task,
      dates: `${startTime.toISOString().replace(/[-:.]/g, "")}Z/${endTime.toISOString().replace(/[-:.]/g, "")}Z`,
    };

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      event.text
    )}&dates=${encodeURIComponent(event.dates)}`;

    window.open(url, "_blank");
  };

  const handleMoveClick = (task: Task, importance: number, urgency: number) => {
    updateTaskPriority.mutate({ taskId: task.id, importance, urgency });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 border rounded-2xl bg-white/70 backdrop-blur-sm space-y-3 animate-fade-in">
            <div className="flex items-start gap-3">
              <Skeleton className="h-5 w-5 rounded flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tasks.map((task, index) => (
        <TaskItem
          key={task.id}
          task={task}
          commentCount={commentQueries[index]?.data}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onCalendarClick={addToGoogleCalendar}
          onCompleteClick={completeTask.mutate}
          onSkipClick={skipOccurrence.mutate}
          onMoveClick={handleMoveClick}
          draggable={draggable}
        />
      ))}
      
      {tasks.length === 0 && !isLoading && (
        <EmptyState
          icon={ListTodo}
          title="No tasks yet"
          description="Your task list is empty. Start by adding your first task to get organized!"
        />
      )}
    </div>
  );
};

export default TaskList;
